import { WHATSAPP_CONSTANTS } from '../constants';
import type { 
  MessageTemplate, 
  PhoneNumber, 
  SendMessageResponse, 
  WebhookSubscriptionResponse 
} from '../types/whatsapp';

export class WhatsAppApiService {
  private static buildUrl(endpoint: string): string {
    return `${WHATSAPP_CONSTANTS.GRAPH_API_BASE}/${WHATSAPP_CONSTANTS.API_VERSION}/${endpoint}`;
  }

  static async exchangeCodeForToken(code: string): Promise<string> {
    const response = await fetch(`${process.env.SUPABASE_URL}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${process.env.SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ code }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error || 'Failed to exchange code for token');
    }
    
    if (!data.access_token) {
      throw new Error('No access token received');
    }
    
    return data.access_token;
  }

  static async subscribeToWebhooks(wabaId: string, accessToken: string): Promise<WebhookSubscriptionResponse> {
    const response = await fetch(this.buildUrl(`${wabaId}/subscribed_apps`), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    const data = await response.json();
    
    if (!data.success) {
      throw new Error('Failed to subscribe to webhooks');
    }
    
    return data;
  }

  static async getPhoneNumbers(wabaId: string, accessToken: string): Promise<PhoneNumber[]> {
    const response = await fetch(this.buildUrl(`${wabaId}/phone_numbers`), {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    const data = await response.json();
    
    if (!data.data || data.data.length === 0) {
      throw new Error('No phone numbers found');
    }
    
    return data.data;
  }

  static async sendMessage(
    phoneNumberId: string, 
    accessToken: string, 
    to: string, 
    message: string
  ): Promise<SendMessageResponse> {
    const response = await fetch(this.buildUrl(`${phoneNumberId}/messages`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        messaging_product: 'whatsapp',
        recipient_type: 'individual',
        to,
        type: 'text',
        text: { body: message }
      })
    });
    
    const data = await response.json();
    
    if (!data.messages || data.messages.length === 0) {
      throw new Error('Failed to send message');
    }
    
    return data;
  }

  static async getMessageTemplates(wabaId: string, accessToken: string): Promise<MessageTemplate[]> {
    const response = await fetch(this.buildUrl(`${wabaId}/message_templates`), {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to get templates');
    }
    
    return data.data || [];
  }

  static async createMessageTemplate(
    wabaId: string, 
    accessToken: string, 
    templateData: {
      name: string;
      language: string;
      category: string;
      components: any[];
    }
  ): Promise<any> {
    const response = await fetch(this.buildUrl(`${wabaId}/message_templates`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify(templateData)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to create template');
    }
    
    return data;
  }

  static async sendTemplateMessage(
    phoneNumberId: string,
    accessToken: string,
    phoneNumber: string,
    templateName: string,
    languageCode: string
  ): Promise<SendMessageResponse> {
    const response = await fetch(this.buildUrl(`${phoneNumberId}/messages`), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`
      },
      body: JSON.stringify({
        messaging_product: 'whatsapp',
        to: phoneNumber,
        type: 'template',
        template: {
          name: templateName,
          language: { code: languageCode }
        }
      })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.error?.message || 'Failed to send template');
    }
    
    return data;
  }
}